'use client';

import { ElementRef, HTMLAttributes } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
  ref?: React.RefObject<ElementRef<typeof SheetPrimitive.Overlay> | null>;
}) => (
  <SheetPrimitive.Overlay
    className={cn('sheet-overlay bg-overlay fixed inset-0 z-50', className)}
    {...props}
    ref={ref}
  />
);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName || 'SheetOverlay';

const sheetVariants = cva(
  'sheet-content fixed z-50 gap-1 bg-background p-6 shadow-lg',
  {
    variants: {
      side: {
        top: 'sheet-content-top inset-x-0 top-0 border-b',
        bottom: 'sheet-content-bottom inset-x-0 bottom-0 border-t',
        left: 'sheet-content-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
        right:
          'sheet-content-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm'
      }
    },
    defaultVariants: {
      side: 'right'
    }
  }
);

interface SheetContentProps
  extends
    ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /**
   * Whether to show the built-in top-right close (X) button.
   * @default true
   */
  showCloseButton?: boolean;
}

const SheetContent = ({
  ref,
  side = 'right',
  className,
  children,
  showCloseButton = true,
  ...props
}: SheetContentProps & {
  ref?: React.RefObject<ElementRef<typeof SheetPrimitive.Content> | null>;
}) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <SheetPrimitive.Close className="focus:ring-ring/70 data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:outline-hidden disabled:pointer-events-none">
          <X className={tokens.icon()} />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
);
SheetContent.displayName = SheetPrimitive.Content.displayName || 'SheetContent';

const SheetHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SheetPrimitive.Title> & {
  ref?: React.RefObject<ElementRef<typeof SheetPrimitive.Title> | null>;
}) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(tokens.typography.heading.h5, 'text-foreground', className)}
    {...props}
  />
);
SheetTitle.displayName = SheetPrimitive.Title.displayName || 'SheetTitle';

const SheetDescription = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SheetPrimitive.Description> & {
  ref?: React.RefObject<ElementRef<typeof SheetPrimitive.Description> | null>;
}) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn(tokens.typography.body.muted, className)}
    {...props}
  />
);
SheetDescription.displayName =
  SheetPrimitive.Description.displayName || 'SheetDescription';

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription
};
